import { useState, useEffect } from 'react';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useAtom, useAtomValue } from 'jotai';

import { KafkaNote } from '@nf/utils-common/constants';
import { 
	filterAtom, 
	keywordAtom,
	isSelectedOnlyAtom,
	oddsLeaguesForFilterAtom,
	filteredResultAtom
} from '@/atoms/league-filter';
import { stringEmptyCheck } from '@nf/utils-common/fpw/utils';
import { useMenuSportName } from '@/hooks/query';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';

import { useLeagueFilter } from '@/features/league-filter/hooks/use-league-filter';
import { SportIcon } from '@/components/icons/svg-icons';
import { SearchInput } from '../search-input';
import { LabelCheckButton } from '../label-check-button';

export const LeagueFilterHeader = () => {
	const t = useTranslations('General');
	const params = useParams();
	const filter = useAtomValue(filterAtom);
	const keyword = useAtomValue(keywordAtom);

	const oddsLeagues = useAtomValue(oddsLeaguesForFilterAtom);
	const filteredResult = useAtomValue(filteredResultAtom);
	const sportName = useMenuSportName(filter.sport);

	const [isSelectedOnly, setIsSelectedOnly] = useAtom(isSelectedOnlyAtom);
	const [isCheckAllSearchResult, setIsCheckAllSearchResult] = useState(
		filteredResult.every(leagueItem => leagueItem.checked)
	);

	const { 
		tempSelectAllChecked, 
		setTempSelectAllChecked, 
		tempSelectedLeagues, 
		setTempSelectedLeagues,
		tempLeagueSelection
	} = useLeagueFilter();

	const tempFilteredResult = filteredResult.map(leagueItem => ({
		...leagueItem,
		checked: Boolean(
			tempSelectedLeagues.find(item => item.leagueId === leagueItem.leagueId)?.checked
		)
	}));

	const handleCheckAll = (event: React.MouseEvent) => {
		event.preventDefault();
		setTempSelectAllChecked(prevIsChecked => !prevIsChecked);

		if (!tempSelectAllChecked) {
			setIsSelectedOnly(false);
		}

		const leagueList = tempSelectedLeagues.map(item => ({
			...item,
			checked: !tempSelectAllChecked
		}));
		setTempSelectedLeagues(leagueList);

		if (params.sport === '1') {
			setKafkaNormal({
				note: KafkaNote.MarsV1,
				type: 3,
				action: 11
			});
		}
	};

	const handleSelectedOnly = (event: React.MouseEvent) => {
		event.preventDefault();

		if (tempSelectAllChecked) return;

		setIsSelectedOnly(prevIsChecked => !prevIsChecked);

		if (params.sport === '1') {
			setKafkaNormal({
				note: KafkaNote.MarsV1,
				type: 3,
				action: 10
			});
		}
	};

	const handleCheckAllSearchResult = (event: React.MouseEvent) => {
		event.preventDefault();
		setIsCheckAllSearchResult(prevIsChecked => !prevIsChecked);

		const leagueList = tempSelectedLeagues.map(leagueItem => ({
			...leagueItem,
			checked: filteredResult.some(item => item.leagueId === leagueItem.leagueId) 
				? !isCheckAllSearchResult
				: leagueItem.checked
		}));
		setTempSelectedLeagues(leagueList);
	};

	useEffect(() => {
		setIsCheckAllSearchResult(
			tempFilteredResult.length > 0 
				? tempFilteredResult.every(item => item.checked)
				: false
		);
	}, [tempFilteredResult]);

	useEffect(() => {
		const allLeaguesSelected = tempLeagueSelection.length === oddsLeagues.size;
		setTempSelectAllChecked(allLeaguesSelected);
	}, [tempLeagueSelection.length, oddsLeagues.size]);
	
	return (
		<div className="select-league__header">
			<div className="select-league__title">
				<SportIcon sportId={filter.sport} />
				<span className="select-league__title-text">{sportName}</span>
				<span className="select-league__title-text">
					({`${tempLeagueSelection.length}/${oddsLeagues.size}`})
				</span>
			</div>
			<div className="search-input">
				<SearchInput />
			</div>
			<div className="select-league__filter">
				{stringEmptyCheck(keyword).fold(
					() => (
						<>
							<LabelCheckButton
								selected={tempSelectAllChecked}
								onClick={handleCheckAll}
							>
								{t('lbl_LeagueFilter02')}
							</LabelCheckButton>
							<LabelCheckButton
								selected={isSelectedOnly}
								disabled={tempSelectAllChecked}
								onClick={handleSelectedOnly}
							>
								{t('lbl_LeagueFilter03')}
							</LabelCheckButton>
						</>
					),
					() => (
						<LabelCheckButton
							selected={isCheckAllSearchResult}
							disabled={tempFilteredResult.length === 0}
							onClick={handleCheckAllSearchResult}
						>
							{t('lbl_LeagueFilter01')}
						</LabelCheckButton>
					)
				)}
			</div>
		</div>
	);
};